import { supabase } from '../lib/supabase';

const BUCKET = 'site-hero-assets';
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const sanitizeExtension = (name = '') => {
  const extension = name?.split('.')?.pop()?.toLowerCase()?.replace(/[^a-z0-9]/g, '');
  return extension || 'bin';
};

export const validateHeroAsset = (file) => {
  if (!file) throw new Error('Choose an image or video to upload.');

  const isImage = IMAGE_TYPES?.includes(file?.type);
  const isVideo = VIDEO_TYPES?.includes(file?.type);
  if (!isImage && !isVideo) {
    throw new Error('Use a JPG, PNG, WebP, GIF, AVIF, MP4, WebM, or MOV file.');
  }

  const limit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file?.size > limit) {
    throw new Error(`${isVideo ? 'Videos' : 'Images'} must be smaller than ${isVideo ? '50 MB' : '10 MB'}.`);
  }

  return isVideo ? 'video' : 'image';
};

export const getHeroContent = async () => {
  const { data, error } = await supabase
    ?.from('site_hero_content')
    ?.select('config, updated_at')
    ?.eq('id', 'homepage')
    ?.maybeSingle();

  if (error) throw error;
  return data ? { ...data?.config, updatedAt: data?.updated_at } : null;
};

export const saveHeroContent = async (config) => {
  const { data, error } = await supabase?.rpc('save_site_hero_content', {
    p_config: config,
  });

  if (error) throw error;
  return data?.config || config;
};

export const uploadHeroAsset = async (file, sectionId = 'shared') => {
  const mediaType = validateHeroAsset(file);
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) throw new Error('You must be signed in to upload hero media.');

  const safeSection = String(sectionId || 'shared')?.replace(/[^a-zA-Z0-9_-]/g, '-')?.slice(0, 80);
  const filePath = `${user?.id}/${safeSection}/${crypto?.randomUUID?.() || Date.now()}.${sanitizeExtension(file?.name)}`;
  const { error } = await supabase?.storage?.from(BUCKET)?.upload(filePath, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: file?.type,
  });

  if (error) throw error;
  const { data } = supabase?.storage?.from(BUCKET)?.getPublicUrl(filePath);
  return { mediaType, mediaUrl: data?.publicUrl, filePath };
};

export const removeHeroAssets = async (filePaths) => {
  const paths = (filePaths || [])?.filter(Boolean);
  if (!paths?.length) return;
  const { error } = await supabase?.storage?.from(BUCKET)?.remove(paths);
  if (error) throw error;
};

export const heroContentService = {
  getHeroContent,
  saveHeroContent,
  uploadHeroAsset,
  removeHeroAssets,
  validateHeroAsset,
};